import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  BarChart3, Users, GraduationCap, TrendingUp, FileText, 
  ClipboardList, Send, LogOut, Globe, Menu, X, Loader2,
  CheckCircle, MessageSquare, Activity, Trash2, EyeOff, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Overview Panel
const OverviewPanel = ({ stats }) => {
  const { t } = useTranslation();
  
  const metrics = [
    { label: t('totalTrainers') || 'Total Trainers', value: stats.totalTrainers, icon: GraduationCap, color: 'text-blue-600 bg-blue-100' },
    { label: t('totalTrainees') || 'Total Trainees', value: stats.totalTrainees, icon: Users, color: 'text-green-600 bg-green-100' },
    { label: t('activeCourses') || 'Active Courses', value: stats.totalCourses, icon: ClipboardList, color: 'text-purple-600 bg-purple-100' },
    { label: t('totalExams') || 'Total Exams', value: stats.totalExams, icon: FileText, color: 'text-orange-600 bg-orange-100' },
    { label: t('avgAttendance') || 'Avg Attendance', value: `${stats.avgAttendance}%`, icon: CheckCircle, color: 'text-teal-600 bg-teal-100' },
    { label: t('avgParticipation') || 'Avg Participation', value: `${stats.avgParticipation}%`, icon: Activity, color: 'text-pink-600 bg-pink-100' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t('analyticsOverview') || 'Analytics Overview'}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metrics.map((m, i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 rounded-full ${m.color} flex items-center justify-center mx-auto mb-2`}>
                <m.icon className="h-5 w-5" />
              </div>
              <div className="text-2xl font-bold">{m.value}</div>
              <div className="text-xs text-gray-500">{m.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// KPIs Panel
const KPIsPanel = ({ kpis }) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">{t('keyPerformanceIndicators') || 'Key Performance Indicators'}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">{t('topCourses') || 'Most Requested Courses'}</CardTitle>
          </CardHeader>
          <CardContent>
            {kpis.topCourses?.length > 0 ? (
              <ul className="space-y-2">
                {kpis.topCourses.map((c, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span>{c.title}</span>
                    <span className="text-gray-500">{c.enrollments} enrolled</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-gray-400 text-sm">No data</p>}
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">{t('activeTrainers') || 'Most Active Trainers'}</CardTitle>
          </CardHeader>
          <CardContent>
            {kpis.activeTrainers?.length > 0 ? (
              <ul className="space-y-2">
                {kpis.activeTrainers.map((t, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span>{t.name}</span>
                    <span className="text-gray-500">{t.courses} courses</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-gray-400 text-sm">No data</p>}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">{t('satisfactionMetrics') || 'Satisfaction Metrics'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm">Trainee Satisfaction</span>
                <span className="font-bold text-green-600">{kpis.traineeSatisfaction || 0}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Trainer Satisfaction</span>
                <span className="font-bold text-blue-600">{kpis.trainerSatisfaction || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">{t('platformUsage') || 'Platform Usage'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm">Avg Session Duration</span>
                <span className="font-bold">{kpis.avgSessionDuration || 0} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Active Users (7d)</span>
                <span className="font-bold">{kpis.activeUsers7d || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Enhanced Surveys Panel
const SurveysPanel = ({ surveys, onCreateSurvey, onToggleSurvey, onDeleteSurvey, onDownloadReport, onRefresh }) => {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    description: '',
    target_audience: 'trainees',
    questions: [{ text: '', type: 'rating' }]
  });

  const handleAddQuestion = () => {
    setNewSurvey({
      ...newSurvey,
      questions: [...newSurvey.questions, { text: '', type: 'rating' }]
    });
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...newSurvey.questions];
    updated[index][field] = value;
    setNewSurvey({ ...newSurvey, questions: updated });
  };

  const handleRemoveQuestion = (index) => {
    if (newSurvey.questions.length > 1) {
      const updated = newSurvey.questions.filter((_, i) => i !== index);
      setNewSurvey({ ...newSurvey, questions: updated });
    }
  };

  const handleCreate = async () => {
    if (!newSurvey.title || newSurvey.questions.some(q => !q.text)) {
      toast.error(t('fillAllFields') || 'Please fill all fields');
      return;
    }
    setCreating(true);
    try {
      await onCreateSurvey(newSurvey);
      setShowCreate(false);
      setNewSurvey({ title: '', description: '', target_audience: 'trainees', questions: [{ text: '', type: 'rating' }] });
      toast.success(t('surveyCreated') || 'Survey created and sent');
      onRefresh();
    } catch (e) {
      toast.error(t('error') || 'Failed to create survey');
    }
    setCreating(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{t('surveys') || 'Surveys'}</h2>
        <Button onClick={() => setShowCreate(true)} className="bg-indigo-600">
          <ClipboardList className="h-4 w-4 mr-2" />
          {t('createSurvey') || 'Create Survey'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {surveys.map((survey) => (
          <Card key={survey.id} className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="truncate">{survey.title}</span>
                <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${survey.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {survey.is_active ? t('active') || 'Active' : t('closed') || 'Closed'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">{survey.description}</p>
              <div className="flex justify-between text-xs text-gray-400 mb-3">
                <span>{t('target') || 'Target'}: {survey.target_audience}</span>
                <span>{survey.responses_count || 0} {t('responses') || 'responses'}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onToggleSurvey(survey.id, !survey.is_active)}
                  className={survey.is_active ? 'text-orange-600 border-orange-300' : 'text-green-600 border-green-300'}
                >
                  {survey.is_active ? <EyeOff className="h-3 w-3 mr-1" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  {survey.is_active ? t('hide') || 'Hide' : t('activate') || 'Activate'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDownloadReport(survey.id)}
                  className="text-blue-600 border-blue-300"
                  disabled={!survey.responses_count}
                >
                  <Download className="h-3 w-3 mr-1" />
                  {t('report') || 'Report'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDeleteSurvey(survey.id)}
                  className="text-red-600 border-red-300"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {t('delete') || 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {surveys.length === 0 && (
          <p className="text-gray-400 col-span-2 text-center py-8">{t('noSurveys') || 'No surveys yet'}</p>
        )}
      </div>

      {/* Create Survey Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('createSurvey') || 'Create Survey'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder={t('surveyTitle') || 'Survey Title'}
              value={newSurvey.title}
              onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
            />
            <Textarea
              placeholder={t('description') || 'Description'}
              value={newSurvey.description}
              onChange={(e) => setNewSurvey({ ...newSurvey, description: e.target.value })}
            />
            <Select value={newSurvey.target_audience} onValueChange={(v) => setNewSurvey({ ...newSurvey, target_audience: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trainees">{t('trainees') || 'Trainees'}</SelectItem>
                <SelectItem value="trainers">{t('trainers') || 'Trainers'}</SelectItem>
                <SelectItem value="all">{t('allUsers') || 'All Users'}</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('questions') || 'Questions'}</label>
              {newSurvey.questions.map((q, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    placeholder={`${t('question') || 'Question'} ${i + 1}`}
                    value={q.text}
                    onChange={(e) => handleQuestionChange(i, 'text', e.target.value)}
                    className="flex-1"
                  />
                  <Select value={q.type} onValueChange={(v) => handleQuestionChange(i, 'type', v)}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rating">{t('rating') || 'Rating'}</SelectItem>
                      <SelectItem value="choice">{t('choice') || 'Choice'}</SelectItem>
                      <SelectItem value="text">{t('text') || 'Text'}</SelectItem>
                    </SelectContent>
                  </Select>
                  {newSurvey.questions.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveQuestion(i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={handleAddQuestion}>
                + {t('addQuestion') || 'Add Question'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t('cancel') || 'Cancel'}</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {t('createAndSend') || 'Create & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reports Panel (Same as Teacher Reports)
const ReportsPanel = () => {
  const { t } = useTranslation();
  const [trainees, setTrainees] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedTrainee, setSelectedTrainee] = useState('');
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [loadingTrainee, setLoadingTrainee] = useState(false);
  const [loadingInstructor, setLoadingInstructor] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [traineesRes, instructorsRes] = await Promise.all([
        axios.get(`${API}/teacher/students`),
        axios.get(`${API}/reports/instructors`)
      ]);
      setTrainees(traineesRes.data.students || []);
      setInstructors(instructorsRes.data.instructors || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const downloadTraineeReport = async () => {
    if (!selectedTrainee) {
      toast.error(t('selectTrainee') || 'Please select a trainee');
      return;
    }
    setLoadingTrainee(true);
    try {
      const response = await axios.get(`${API}/reports/trainee/${selectedTrainee}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const trainee = trainees.find(t => t.id === selectedTrainee);
      link.download = `trainee_report_${trainee?.name || selectedTrainee}.pdf`;
      link.click();
      toast.success(t('reportDownloaded') || 'Report downloaded');
    } catch (error) {
      toast.error(t('error') || 'Error');
    }
    setLoadingTrainee(false);
  };

  const downloadInstructorReport = async () => {
    if (!selectedInstructor) {
      toast.error(t('selectInstructor') || 'Please select an instructor');
      return;
    }
    setLoadingInstructor(true);
    try {
      const response = await axios.get(`${API}/reports/instructor/${selectedInstructor}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const instructor = instructors.find(i => i.id === selectedInstructor);
      link.download = `instructor_report_${instructor?.name || selectedInstructor}.pdf`;
      link.click();
      toast.success(t('reportDownloaded') || 'Report downloaded');
    } catch (error) {
      toast.error(t('error') || 'Error');
    }
    setLoadingInstructor(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t('reports') || 'Reports'}</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Trainee Reports */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {t('traineeReports') || 'Trainee Reports'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-gray-600">
              {t('traineeReportDesc') || 'Select a trainee to generate PDF report'}
            </p>
            <Select value={selectedTrainee} onValueChange={setSelectedTrainee}>
              <SelectTrigger>
                <SelectValue placeholder={t('selectTrainee') || 'Select Trainee'} />
              </SelectTrigger>
              <SelectContent>
                {trainees.map((trainee) => (
                  <SelectItem key={trainee.id} value={trainee.id}>
                    {trainee.name} ({trainee.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadTraineeReport} disabled={!selectedTrainee || loadingTrainee} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
              {loadingTrainee ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {t('downloadReport') || 'Download Report'}
            </Button>
          </CardContent>
        </Card>

        {/* Instructor Reports */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-green-600" />
              {t('instructorReports') || 'Instructor Reports'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-gray-600">
              {t('instructorReportDesc') || 'Select an instructor to generate PDF report'}
            </p>
            <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
              <SelectTrigger>
                <SelectValue placeholder={t('selectInstructor') || 'Select Instructor'} />
              </SelectTrigger>
              <SelectContent>
                {instructors.map((instructor) => (
                  <SelectItem key={instructor.id} value={instructor.id}>
                    {instructor.name} ({instructor.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadInstructorReport} disabled={!selectedInstructor || loadingInstructor} className="w-full bg-gradient-to-r from-green-600 to-emerald-600">
              {loadingInstructor ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {t('downloadReport') || 'Download Report'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Recommendations Panel
const RecommendationsPanel = ({ onSendRecommendation }) => {
  const { t } = useTranslation();
  const [recommendation, setRecommendation] = useState({ text: '', target: 'admin' });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!recommendation.text) {
      toast.error(t('enterText') || 'Please enter recommendation text');
      return;
    }
    setSending(true);
    try {
      await onSendRecommendation(recommendation);
      setRecommendation({ text: '', target: 'admin' });
      toast.success(t('recommendationSent') || 'Recommendation sent');
    } catch (e) {
      toast.error(t('error') || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">{t('recommendations') || 'Recommendations & Insights'}</h2>
      <Card className="border-0 shadow-md">
        <CardContent className="p-6 space-y-4">
          <Textarea
            placeholder={t('writeRecommendation') || 'Write your recommendation or insight...'}
            value={recommendation.text}
            onChange={(e) => setRecommendation({ ...recommendation, text: e.target.value })}
            rows={4}
          />
          <div className="flex gap-4 items-center flex-wrap">
            <Select value={recommendation.target} onValueChange={(v) => setRecommendation({ ...recommendation, target: v })}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{t('admin') || 'Admin'}</SelectItem>
                <SelectItem value="trainers">{t('allTrainers') || 'All Trainers'}</SelectItem>
                <SelectItem value="trainees">{t('allTrainees') || 'All Trainees'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSend} disabled={sending} className="bg-indigo-600">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              {t('sendRecommendation') || 'Send Recommendation'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Analytics Reports Panel
const AnalyticsReportsPanel = ({ onGenerateReport }) => {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('performance');

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await onGenerateReport(reportType);
      toast.success(t('reportGenerated') || 'Report generated');
    } catch (e) {
      toast.error(t('error') || 'Failed to generate report');
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">{t('analyticsReports') || 'Analytics Reports'}</h2>
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            {t('analyticsReportDesc') || 'Generate platform-wide analytics reports'}
          </p>
          <div className="flex gap-4 items-center flex-wrap">
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="performance">{t('performanceSummary') || 'Performance Summary'}</SelectItem>
                <SelectItem value="satisfaction">{t('satisfactionMetrics') || 'Satisfaction Metrics'}</SelectItem>
                <SelectItem value="usage">{t('usageAnalysis') || 'Usage Analysis'}</SelectItem>
                <SelectItem value="trends">{t('identifiedTrends') || 'Identified Trends'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleGenerate} disabled={generating} className="bg-green-600">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              {t('generatePDFReport') || 'Generate PDF Report'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main LAO Dashboard
const LAODashboard = ({ user, onLogout, onToggleLanguage }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [kpis, setKpis] = useState({});
  const [surveys, setSurveys] = useState([]);

  const navItems = [
    { path: '/lao', icon: BarChart3, label: t('overview') || 'Overview', exact: true },
    { path: '/lao/kpis', icon: TrendingUp, label: t('kpis') || 'KPIs' },
    { path: '/lao/surveys', icon: ClipboardList, label: t('surveys') || 'Surveys' },
    { path: '/lao/reports', icon: FileText, label: t('reports') || 'Reports' },
    { path: '/lao/analytics-reports', icon: BarChart3, label: t('analyticsReports') || 'Analytics Reports' },
    { path: '/lao/recommendations', icon: MessageSquare, label: t('recommendations') || 'Recommendations' },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, kpisRes, surveysRes] = await Promise.all([
        axios.get(`${API}/lao/stats`),
        axios.get(`${API}/lao/kpis`),
        axios.get(`${API}/lao/surveys`)
      ]);
      setStats(statsRes.data);
      setKpis(kpisRes.data);
      setSurveys(surveysRes.data.surveys || []);
    } catch (e) {
      console.error('Error fetching LAO data:', e);
    }
    setLoading(false);
  };

  const createSurvey = async (surveyData) => {
    await axios.post(`${API}/lao/surveys`, surveyData);
  };

  const toggleSurvey = async (surveyId, isActive) => {
    await axios.patch(`${API}/lao/surveys/${surveyId}`, { is_active: isActive });
    fetchData();
    toast.success(isActive ? t('surveyActivated') || 'Survey activated' : t('surveyHidden') || 'Survey hidden');
  };

  const deleteSurvey = async (surveyId) => {
    if (!window.confirm(t('confirmDeleteSurvey') || 'Are you sure you want to delete this survey?')) return;
    await axios.delete(`${API}/lao/surveys/${surveyId}`);
    fetchData();
    toast.success(t('surveyDeleted') || 'Survey deleted');
  };

  const downloadSurveyReport = async (surveyId) => {
    const response = await axios.get(`${API}/lao/surveys/${surveyId}/report`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `survey_report_${surveyId}.pdf`;
    link.click();
    toast.success(t('reportDownloaded') || 'Report downloaded');
  };

  const sendRecommendation = async (recData) => {
    await axios.post(`${API}/lao/recommendations`, recData);
  };

  const generateReport = async (reportType) => {
    const response = await axios.get(`${API}/lao/reports/${reportType}`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Top Bar */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <img src="/logo.png" alt="Shamah" className="h-10 w-10 object-contain" />
              <span className="font-semibold text-indigo-600 hidden sm:block">Learning Analytics</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 hidden sm:block">{user.name}</span>
              <Button onClick={onToggleLanguage} variant="outline" size="icon" className="rounded-full">
                <Globe className="h-5 w-5" />
              </Button>
              <Button onClick={onLogout} variant="outline" size="icon" className="rounded-full text-red-600">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r shadow-lg transform transition-transform z-40 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <nav className="h-full overflow-y-auto p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${active ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-[calc(100vh-4rem)]">
          <Routes>
            <Route path="/" element={<OverviewPanel stats={stats} />} />
            <Route path="/kpis" element={<KPIsPanel kpis={kpis} />} />
            <Route path="/surveys" element={
              <SurveysPanel 
                surveys={surveys} 
                onCreateSurvey={createSurvey} 
                onToggleSurvey={toggleSurvey}
                onDeleteSurvey={deleteSurvey}
                onDownloadReport={downloadSurveyReport}
                onRefresh={fetchData} 
              />
            } />
            <Route path="/reports" element={<ReportsPanel />} />
            <Route path="/analytics-reports" element={<AnalyticsReportsPanel onGenerateReport={generateReport} />} />
            <Route path="/recommendations" element={<RecommendationsPanel onSendRecommendation={sendRecommendation} />} />
          </Routes>
        </main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default LAODashboard;
